/**
 * This component is a wrapper around the Link component from react-resource-router,
 * which sends an analytics event when the link or button is clicked or hovered.
 */
import React, { KeyboardEvent, MouseEvent, useContext } from 'react';
import { Link, LinkProps } from 'react-resource-router';
import { UITrackEventPayload } from '@atlassian/community-library';
import { UserContext } from 'context/UserContext';
import { sendUIEvent, sendConversionOperationalEvent } from 'services/analytics';
import type { OperationalEventPayload } from './LinkData';

interface LinkWithAnalyticsProps extends LinkProps {
  clickEventPayload?: UITrackEventPayload;
  hoverEventPayload?: UITrackEventPayload;
  operationalClickEventPayload?: OperationalEventPayload;
}

export const LinkWithAnalytics: React.FC<LinkWithAnalyticsProps> = ({
  onClick,
  onMouseEnter,
  clickEventPayload,
  hoverEventPayload,
  operationalClickEventPayload,
  href,
  ...rest
}) => {
  const user = useContext(UserContext);

  const handleClick = (
    e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
  ): void => {
    // Always prevent the default action to control navigation manually
    e.preventDefault();

    // Fire the UI track event and any operational (strictly-necessary) event
    // together, and wait for both to flush before navigating. This matters for
    // full-page redirects (e.g. login), where in-flight beacons can be dropped.
    const analyticsSends: Array<Promise<void>> = [];

    if (clickEventPayload) {
      const { action, actionSubject, actionSubjectId, attributes, source } =
        clickEventPayload;
      analyticsSends.push(
        sendUIEvent(
          source,
          actionSubject,
          action,
          actionSubjectId,
          attributes,
          user?.user?.account_id,
        ),
      );
    }

    if (operationalClickEventPayload) {
      const { source, actionSubject, action, attributes } =
        operationalClickEventPayload;
      // Identity is excluded downstream by sendConversionOperationalEvent.
      analyticsSends.push(
        sendConversionOperationalEvent(source, actionSubject, action, attributes),
      );
    }

    if (rest.target === '_blank') {
      // Open in a new tab/window; the current page stays put, so no need to
      // block navigation on analytics.
      void Promise.allSettled(analyticsSends);
      window.open(href, '_blank', 'noopener noreferrer');
      return;
    }

    const navigate = (): void => {
      if (href) {
        window.location.href = href;
      }
    };

    if (analyticsSends.length === 0) {
      navigate();
    } else if (analyticsSends.length === 1) {
      // Preserve single-event timing: chain directly off the one send.
      void analyticsSends[0].then(navigate, navigate);
    } else {
      // Multiple sends (UI + operational): flush both, then navigate.
      void Promise.allSettled(analyticsSends).then(navigate);
    }
  };
  const handleHover = (e: MouseEvent<Element, globalThis.MouseEvent>): void => {
    if (hoverEventPayload) {
      const { action, actionSubject, actionSubjectId, attributes, source } =
        hoverEventPayload;
      sendUIEvent(
        source,
        actionSubject,
        action,
        actionSubjectId,
        attributes,
        user?.user?.account_id,
      );
    }
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  return (
    <Link
      {...rest}
      href={href}
      onClick={handleClick}
      onMouseEnter={handleHover}
    />
  );
};
